import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Facebook, Linkedin, MapPin, Phone, Mail, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import axios from "axios";
import SEO, { LOCAL_BUSINESS_SCHEMA } from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", event_type: "", reason: "", message: "", newsletter: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const locale = i18n.language?.startsWith("en") ? "en" : "it";
      await axios.post(`${API}/contact`, { ...formData, locale });
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", event_type: "", reason: "", message: "", newsletter: false });
    } catch (err) {
      setError(t("contact.form_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const eventOptions = ["matrimonio", "moda", "cinema", "evento", "altro"];
  const reasonOptions = ["morgan", "su_misura", "limited", "press", "altro"];

  return (
    <div data-testid="contact-page">
      <SEO titleKey="seo.contact_title" descriptionKey="seo.contact_description" path="/contact" schema={LOCAL_BUSINESS_SCHEMA} />

      <section className="pt-32 pb-12 lg:pt-40 lg:pb-16" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
              {t("contact.eyebrow")}
            </p>
          </motion.div>
          <motion.h1 initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1B1A] max-w-3xl"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
            {t("contact.title_1")} <span style={{ fontWeight: 600 }}>{t("contact.title_2")}</span>
          </motion.h1>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="lg:col-span-7">
              {submitted ? (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-16 text-center" data-testid="contact-success">
                  <div className="luxury-divider mx-auto mb-8" />
                  <h3 className="text-2xl sm:text-3xl tracking-tighter text-[#1A1B1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                    {t("contact.success_title")}
                  </h3>
                  <p className="text-sm text-[#4A4A4A] max-w-md mx-auto" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                    {t("contact.success_subtitle")}
                  </p>
                  <button onClick={() => setSubmitted(false)} className="btn-luxury-outline mt-8" data-testid="send-another-btn">
                    {t("contact.send_another")}
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                        {t("contact.form_name")} *
                      </Label>
                      <Input id="name" data-testid="contact-name-input" value={formData.name} onChange={(e) => handleChange("name", e.target.value)} required
                        className="rounded-none border-[#D8D4CC] bg-transparent focus:border-[#C4A462] focus:ring-0 h-12 text-sm"
                        style={{ fontFamily: "'DM Sans', sans-serif" }} placeholder={t("contact.form_name_placeholder")} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                        {t("contact.form_email")} *
                      </Label>
                      <Input id="email" type="email" data-testid="contact-email-input" value={formData.email} onChange={(e) => handleChange("email", e.target.value)} required
                        className="rounded-none border-[#D8D4CC] bg-transparent focus:border-[#C4A462] focus:ring-0 h-12 text-sm"
                        style={{ fontFamily: "'DM Sans', sans-serif" }} placeholder={t("contact.form_email_placeholder")} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.15em] text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                      {t("contact.form_reason")} *
                    </Label>
                    <Select value={formData.reason} onValueChange={(val) => handleChange("reason", val)} required>
                      <SelectTrigger data-testid="contact-reason-select" className="rounded-none border-[#D8D4CC] bg-transparent focus:border-[#C4A462] focus:ring-0 h-12 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                        <SelectValue placeholder={t("contact.form_reason_placeholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {reasonOptions.map((k) => (
                          <SelectItem key={k} value={k} data-testid={`contact-reason-option-${k}`}>
                            {t(`contact.form_reason_options.${k}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-xs uppercase tracking-[0.15em] text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                        {t("contact.form_phone")}
                      </Label>
                      <Input id="phone" data-testid="contact-phone-input" value={formData.phone} onChange={(e) => handleChange("phone", e.target.value)}
                        className="rounded-none border-[#D8D4CC] bg-transparent focus:border-[#C4A462] focus:ring-0 h-12 text-sm"
                        style={{ fontFamily: "'DM Sans', sans-serif" }} placeholder={t("contact.form_phone_placeholder")} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.15em] text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                        {t("contact.form_event_type")}
                      </Label>
                      <Select value={formData.event_type} onValueChange={(val) => handleChange("event_type", val)}>
                        <SelectTrigger data-testid="contact-event-select" className="rounded-none border-[#D8D4CC] bg-transparent focus:border-[#C4A462] focus:ring-0 h-12 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                          <SelectValue placeholder={t("contact.form_event_placeholder")} />
                        </SelectTrigger>
                        <SelectContent>
                          {eventOptions.map((k) => (
                            <SelectItem key={k} value={k}>{t(`contact.form_event_options.${k}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-xs uppercase tracking-[0.15em] text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                      {t("contact.form_message")} *
                    </Label>
                    <Textarea id="message" data-testid="contact-message-input" value={formData.message} onChange={(e) => handleChange("message", e.target.value)} required rows={6}
                      className="rounded-none border-[#D8D4CC] bg-transparent focus:border-[#C4A462] focus:ring-0 text-sm resize-none"
                      style={{ fontFamily: "'DM Sans', sans-serif" }} placeholder={t("contact.form_message_placeholder")} />
                  </div>

                  {error && <p data-testid="contact-error" className="text-sm text-red-600">{error}</p>}

                  <div className="flex items-start gap-3 pt-2">
                    <Checkbox id="contact-newsletter" data-testid="contact-newsletter-checkbox" checked={formData.newsletter} onCheckedChange={(val) => handleChange("newsletter", !!val)}
                      className="mt-0.5 border-[#D8D4CC] data-[state=checked]:bg-[#C4A462] data-[state=checked]:border-[#C4A462] data-[state=checked]:text-[#FAF9F6] rounded-none" />
                    <Label htmlFor="contact-newsletter" className="text-xs text-[#4A4A4A] leading-relaxed cursor-pointer" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                      {t("contact.form_newsletter")}
                    </Label>
                  </div>

                  <button type="submit" data-testid="contact-submit-button" disabled={submitting}
                    className="btn-luxury flex items-center gap-3 disabled:opacity-50">
                    {submitting ? t("contact.form_submitting") : t("contact.form_submit")}
                    <Send strokeWidth={1.25} size={16} />
                  </button>
                </form>
              )}
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={2} className="lg:col-span-5">
              <div className="sticky top-32">
                <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-8" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                  {t("contact.info_eyebrow")}
                </p>
                <div className="space-y-8 mb-12">
                  {[
                    { icon: MapPin, label: t("contact.info_address"), value: t("contact.info_address_value") },
                    { icon: Phone, label: t("contact.info_phone"), value: "+39 348 270 4976" },
                    { icon: Mail, label: t("contact.info_email"), value: "info@eternamotion.com" },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-start gap-4" data-testid={`contact-info-${label.toLowerCase()}`}>
                      <Icon strokeWidth={1.25} size={20} className="text-[#C4A462] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs uppercase tracking-[0.15em] text-[#757575] mb-1" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>{label}</p>
                        <p className="text-sm text-[#1A1B1A]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}>{value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-6" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                    {t("contact.social_eyebrow")}
                  </p>
                  <div className="flex gap-6">
                    {[{ icon: Instagram, label: "Instagram" }, { icon: Facebook, label: "Facebook" }, { icon: Linkedin, label: "LinkedIn" }].map(({ icon: Icon, label }) => (
                      <a key={label} href="#" data-testid={`contact-social-${label.toLowerCase()}`}
                        className="flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#C4A462] transition-colors duration-300"
                        style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}>
                        <Icon strokeWidth={1.25} size={18} />{label}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="mt-12 pt-8 border-t border-[#D8D4CC]/40">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                    {t("contact.hours_eyebrow")}
                  </p>
                  <div className="space-y-2" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A4A4A]">{t("contact.hours_weekdays")}</span><span className="text-[#1A1B1A]">09:00 - 18:00</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A4A4A]">{t("contact.hours_saturday")}</span><span className="text-[#1A1B1A]">10:00 - 14:00</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4A4A4A]">{t("contact.hours_sunday")}</span><span className="text-[#1A1B1A]">{t("contact.hours_appointment")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
